import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import TabellinoModal from "@/components/TabellinoModal";
import TavolinoBadge from "@/components/TavolinoBadge";
import { matchLegLabel } from "@/lib/competitions";

/**
 * TeamRecentResults — striscia delle ultime partite GIOCATE di una squadra,
 * con risultati ed esito (V/N/P). Mostrata sotto le prossime partite nella scheda squadra.
 */
const OUTCOME = {
    W: { label: "V", color: "rgb(var(--tc-7ce38b))", border: "rgb(var(--tc-7ce38b))" },
    D: { label: "N", color: "rgb(var(--tc-e8ff00))", border: "rgb(var(--tc-e8ff00))" },
    L: { label: "P", color: "rgb(var(--tc-ff4d4d))", border: "rgb(var(--tc-ff4d4d))" },
};

export default function TeamRecentResults({ teamId, limit = 8 }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openMatchId, setOpenMatchId] = useState(null);
    const [page, setPage] = useState(1);

    useEffect(() => { setPage(1); }, [teamId]);

    useEffect(() => {
        if (!teamId) return undefined;
        let active = true;
        api.get(`/teams/${teamId}/recent-results`, { params: { limit, offset: (page - 1) * limit } })
            .then(({ data: d }) => { if (active) setData(d); })
            .catch(() => { if (active) setData({ matches: [] }); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [teamId, limit, page]);

    if (!teamId) return null;
    if (loading) {
        return <div className="op-card p-6 mt-6 op-label text-t8f95a3 animate-pulse">// CARICAMENTO RISULTATI…</div>;
    }
    const matches = data?.matches || [];
    if (matches.length === 0) return null;
    const totalPages = Math.max(1, Math.ceil((data?.total || 0) / limit));

    return (
        <div className="op-card mt-6 overflow-hidden" data-testid="team-recent-results">
            <div className="px-5 py-4 border-b border-t22252a bg-t0e1014 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-2xl">📊</span>
                    <div>
                        <div className="op-label text-[10px] text-tff7a1a">// PARTITE GIOCATE · {data?.total || matches.length}</div>
                        <div className="text-tffffff op-heading text-lg">
                            {page === 1 ? `Forma · ultime ${matches.length}` : `Pagina ${page} di ${totalPages}`}
                        </div>
                    </div>
                </div>
                {/* Striscia forma V/N/P (più recente a sinistra) */}
                <div className="hidden sm:flex items-center gap-1" data-testid="form-strip">
                    {matches.map((m, idx) => {
                        const o = OUTCOME[m.outcome] || OUTCOME.D;
                        return (
                            <span
                                key={`f-${m.match_id}-${idx}`}
                                className="op-label text-[11px] w-6 h-6 flex items-center justify-center border"
                                style={{ color: o.color, borderColor: withAlpha(o.border, "88") }}
                                title={`${m.home_team?.name} ${m.home_score}-${m.away_score} ${m.away_team?.name}`}
                            >
                                {o.label}
                            </span>
                        );
                    })}
                </div>
            </div>

            <ul className="divide-y divide-t16181c">
                {matches.map((m, idx) => (
                    <ResultRow
                        key={`${m.match_id}-${idx}`}
                        item={m}
                        onOpen={() => setOpenMatchId(m.match_id)}
                    />
                ))}
            </ul>

            {totalPages > 1 && <Pager page={page} totalPages={totalPages} onChange={setPage} />}

            {openMatchId && (
                <TabellinoModal
                    matchId={openMatchId}
                    onClose={() => setOpenMatchId(null)}
                    onSaved={() => setOpenMatchId(null)}
                />
            )}
        </div>
    );
}

function ResultRow({ item, onOpen }) {
    const o = OUTCOME[item.outcome] || OUTCOME.D;
    const homeBold = item.is_home;
    return (
        <li
            className="px-5 py-3 hover:bg-t16181c/60 cursor-pointer transition-colors border-l-4"
            style={{ borderLeftColor: o.border }}
            onClick={onOpen}
            data-testid={`team-result-${item.match_id}`}
        >
            <div className="flex items-center gap-3">
                {/* Contesto */}
                <div className="hidden sm:block flex-shrink-0 w-48">
                    <Link
                        to={item.context_link}
                        onClick={(e) => e.stopPropagation()}
                        className="op-label text-[10px] hover:underline truncate block"
                        style={{ color: item.context_color }}
                    >
                        {item.context_label}
                    </Link>
                    {item.leg && (
                        <div className="op-mono text-[9px] text-t5c6370 mt-0.5">{matchLegLabel(item.leg)}</div>
                    )}
                </div>

                {/* Squadre + punteggio */}
                <div className="flex-1 flex items-center gap-2 min-w-0">
                    <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
                        <span className={`text-sm truncate ${homeBold ? "text-tffffff font-semibold" : "text-t8f95a3"}`}>
                            {item.home_team?.name || "—"}
                        </span>
                        {item.home_team?.logo_url && (
                            <img src={item.home_team.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                        )}
                    </div>
                    <span
                        className="op-mono text-sm font-semibold px-2 py-0.5 border flex-shrink-0"
                        style={{ color: o.color, borderColor: withAlpha(o.border, "66") }}
                        data-testid={`team-result-score-${item.match_id}`}
                    >
                        {item.home_score}-{item.away_score}
                    </span>
                    <div className="flex-1 flex items-center gap-2 min-w-0">
                        {item.away_team?.logo_url && (
                            <img src={item.away_team.logo_url} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                        )}
                        <span className={`text-sm truncate ${!homeBold ? "text-tffffff font-semibold" : "text-t8f95a3"}`}>
                            {item.away_team?.name || "—"}
                        </span>
                    </div>
                </div>

                {/* Badge esito */}
                <div className="flex-shrink-0 text-right">
                    <span
                        className="op-label text-[11px] w-7 h-7 inline-flex items-center justify-center border"
                        style={{ color: o.color, borderColor: o.border }}
                    >
                        {o.label}
                    </span>
                </div>
            </div>
            {item.auto_result && (
                <div className="mt-1.5 flex justify-center sm:justify-end">
                    <TavolinoBadge idSuffix={item.match_id} definitive={item.is_definitive} />
                </div>
            )}
        </li>
    );
}

function Pager({ page, totalPages, onChange }) {
    const btn = "op-label text-[11px] min-w-[2rem] h-8 px-2 border transition-colors disabled:opacity-30";
    return (
        <div className="px-5 py-3 border-t border-t22252a flex flex-wrap items-center justify-center gap-1" data-testid="team-results-pager">
            <button type="button" className={`${btn} border-t22252a text-t8f95a3 hover:text-tffffff`} disabled={page === 1}
                onClick={() => onChange(page - 1)} data-testid="team-results-prev">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button key={n} type="button" onClick={() => onChange(n)} data-testid={`team-results-page-${n}`}
                    className={`${btn} ${n === page ? "border-tff7a1a text-tff7a1a bg-tff7a1a/10" : "border-t22252a text-t8f95a3 hover:text-tffffff"}`}>
                    {n}
                </button>
            ))}
            <button type="button" className={`${btn} border-t22252a text-t8f95a3 hover:text-tffffff`} disabled={page === totalPages}
                onClick={() => onChange(page + 1)} data-testid="team-results-next">›</button>
        </div>
    );
}
